import { withProfiler, wrapCreateBrowserRouter } from '@sentry/react'
import { StrictMode } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from 'react-router-dom'
import { SWRConfig } from 'swr'

import { apiFetch } from './api/utils/apiFetch.ts'
import { unwrapSWRResponse } from './api/utils/unwrapSWRResponse.ts'
import AdminAutoImpersonate from './appComponents/AdminAutoImpersonate/index.tsx'
import { ErrorBox, LayoutError } from './components/Layout/LayoutError.tsx'
import { Spinner } from './components/Loading/index.tsx'
import { RedirectToDashboardOrCreateNewWorkspace } from './components/RedirectToDashboardOrCreateNewWorkspace.tsx'
import { RedirectToWorkspacePage } from './components/RedirectToWorkspacePage.tsx'
import { RedirectUnknownWorkspace } from './components/RedirectUnknownWorkspace.tsx'
import { Root } from './Root.tsx'

const createBrowserRouterWithSentry = wrapCreateBrowserRouter(createBrowserRouter)

function LoadingFallback() {
  return <Spinner className="loading--page" />
}

// eslint-disable-next-line react-refresh/only-export-components
export const router = createBrowserRouterWithSentry(
  createRoutesFromElements(
    <>
      <Route
        path="/c"
        element={<Root />}
        ErrorBoundary={LayoutError}
        hydrateFallbackElement={<LoadingFallback />}
      >
        {/* Routes with a minimal layout */}

        {/* Authentication routes */}
        <Route path="signup" lazy={() => import('./features/authentication/SignupPage.tsx')} />
        <Route
          path="verify-email/:authKey"
          lazy={() => import('./features/authentication/VerifyEmailPage.tsx')}
        />
        <Route path="login" lazy={() => import('./features/authentication/LoginPage.tsx')} />
        <Route
          path="2fa/:authKey"
          lazy={() => import('./features/authentication/TwoFactorAuthPage.tsx')}
        />
        <Route
          path="forgot-password"
          lazy={() => import('./features/authentication/ForgotPasswordPage.tsx')}
        />
        <Route
          path="reset-password"
          lazy={() => import('./features/authentication/ResetPasswordPage.tsx')}
        />

        {/* Page used by Cake to create an invoice PDF, we need to match this without calling any API endpoints,
              which would redirect us to /login, as we are not logged in when hitting this page */}
        <Route
          path=":workspace/invoices/convert/:invoice_id"
          lazy={() => import('~/features/invoices/InvoiceConvertPage.tsx')}
        />

        <Route element={<RedirectUnknownWorkspace />}>
          {/*
            If a user visits the bare /c/ page, redirect them to the first
            workspace in their memberships list, or to the create new
            workspace page
          */}
          <Route index element={<RedirectToDashboardOrCreateNewWorkspace />} />

          {/* If a user visits any of these pages, forward them to the same with the workspace slug added to the URL. */}
          <Route path="dashboard" element={<RedirectToDashboardOrCreateNewWorkspace />} />
          <Route path="assemblies" element={<RedirectToWorkspacePage />} />
          <Route path="settings" element={<RedirectToWorkspacePage />} />
          <Route path="template-credentials" element={<RedirectToWorkspacePage />} />
          <Route path="collaborators" element={<RedirectToWorkspacePage />} />
          <Route path="billing" element={<RedirectToWorkspacePage />} />
          <Route path="templates" element={<RedirectToWorkspacePage />} />
          <Route path="templates/new" element={<RedirectToWorkspacePage />} />
          <Route path="addons" element={<RedirectToWorkspacePage />} />
          <Route path="onboarding/file-type" element={<RedirectToWorkspacePage />} />

          {/* Legacy page redirects */}
          <Route
            path="create-new-workspace"
            Component={() => <Navigate to="../create-workspace" />}
          />

          {/* Onboarding routes */}
          <Route
            path=":workspace/onboarding/file-type"
            lazy={() => import('./features/onboarding/FileTypePage.tsx')}
          />
          <Route
            path=":workspace/onboarding/setting-up"
            lazy={() => import('./features/onboarding/SettingThingsUpPage.tsx')}
          />
          <Route
            path=":workspace/onboarding/processing"
            lazy={() => import('./features/onboarding/ProcessingPage.tsx')}
          />
          <Route
            path="create-workspace"
            lazy={() => import('~/features/onboarding/CreateNewWorkspacePage.tsx')}
          />

          {/* Invoice rendering page, does not share a common layout */}
          <Route
            path=":workspace/invoices/:invoice_id"
            lazy={() => import('~/features/invoices/InvoicePage.tsx')}
          />

          <Route
            path="admin/invoices/:invoice_id"
            lazy={() => import('~/features/invoices/InvoicePage.tsx')}
          />

          <Route lazy={() => import(`~/components/Layout/index.tsx`)}>
            <Route path="admin" ErrorBoundary={ErrorBox}>
              <Route
                path="workspaces/:workspaceStatus"
                lazy={() => import('./features/admin/workspaces/AdminWorkspacesPage.tsx')}
              />
              <Route
                path="workspaces"
                lazy={() => import('./features/admin/workspaces/AdminWorkspacesPage.tsx')}
              />
              <Route
                path="workspace/:adminWorkspace"
                lazy={() => import('./features/admin/workspaces/AdminViewWorkspacePage.tsx')}
              />
              <Route
                path="workspace/:adminWorkspace/usage"
                lazy={() => import('~/features/admin/workspaces/AdminWorkspaceUsagePage.tsx')}
              />
              <Route
                path="announcements"
                lazy={() => import('./features/announcements/AdminAnnouncementsPage.tsx')}
              />
              <Route path="assemblies" lazy={() => import('./adminPages/Assemblies/index.tsx')} />
              <Route path="assemblies/:id" lazy={() => import('./pages/AssemblyById/index.tsx')} />
              <Route
                path="coupons"
                lazy={() => import('./features/admin/coupons/AdminCouponsPage.tsx')}
              />

              <Route path="dashboard" lazy={() => import('./adminPages/Dashboard/index.tsx')}>
                <Route
                  path="bi"
                  lazy={() => import('./adminPages/Dashboard/BusinessIntelligence.tsx')}
                />
                <Route index lazy={() => import('./adminPages/Dashboard/Performance.tsx')} />
              </Route>

              <Route
                path="invoices"
                lazy={() => import('./features/invoices/AdminInvoicesPage.tsx')}
              />
              <Route path="ops" lazy={() => import('./adminPages/Ops/index.tsx')} />
              <Route path="plans" lazy={() => import('./adminPages/Plans/index.tsx')} />
              <Route path="revenues" lazy={() => import('./adminPages/Revenues/index.tsx')} />
              <Route
                path="founder-dashboard"
                lazy={() => import('./adminPages/FounderDashboard/index.tsx')}
              />
            </Route>
            <Route
              path="user/settings"
              lazy={() => import('./features/user-settings/UserSettingsPage.tsx')}
              ErrorBoundary={ErrorBox}
            />
            {/* Workspace pages, user facing */}
            <Route path=":workspace" element={<AdminAutoImpersonate />} ErrorBoundary={ErrorBox}>
              <Route index lazy={() => import('~/pages/Dashboard/index.tsx')} />
              <Route
                path="template-credentials"
                lazy={async () => {
                  const { TemplateCredentialsPage } = await import(
                    '~/features/credentials/TemplateCredentialsPage.tsx'
                  )
                  return { Component: TemplateCredentialsPage }
                }}
              >
                <Route
                  index
                  lazy={() => import('~/features/credentials/TemplateCredentialsIndexPage.tsx')}
                />
                <Route
                  path="create"
                  lazy={() => import('~/features/credentials/CreateThirdPartyCredentialPage.tsx')}
                />
                <Route
                  path=":credentialId"
                  lazy={() => import('~/features/credentials/EditThirdPartyCredentialPage.tsx')}
                />
                <Route
                  path="create-api-credential"
                  lazy={() => import('~/features/credentials/CreateEditAuthKeyPage.tsx')}
                />
                <Route
                  path="edit-api-credential/:credentialId"
                  lazy={() => import('~/features/credentials/CreateEditAuthKeyPage.tsx')}
                />
                <Route
                  path="show-api-credential-secret/:credentialId"
                  lazy={() => import('~/features/credentials/ShowAuthKeySecretPage.tsx')}
                />
              </Route>
              <Route
                path="templates/new"
                lazy={() => import('~/features/templates/NewTemplatePage/index.tsx')}
              />
              <Route
                path="templates/:id"
                lazy={() => import('~/features/templates/TemplatePage/TemplatePage.tsx')}
              />
              <Route
                path="templates"
                lazy={() => import('~/features/templates/TemplatesPage/index.tsx')}
              />
              <Route path="assemblies" lazy={() => import('~/pages/Assemblies/index.tsx')} />
              <Route path="assemblies/:id" lazy={() => import('./pages/AssemblyById/index.tsx')} />
              <Route path="settings" lazy={() => import('~/pages/Settings/index.tsx')} />
              <Route path="collaborators" lazy={() => import('~/pages/Collaborators/index.tsx')} />
              <Route path="billing" lazy={() => import('~/pages/Billing/index.tsx')} />
              <Route path="addons" lazy={() => import('~/pages/Addons.tsx')} />

              {/* The /:workspace route already matched at this point, it won't continue to the 404 page,
  so we need to specify it again here. */}
              <Route path="*" lazy={() => import('./pages/Error404/index.tsx')} />
            </Route>
          </Route>
        </Route>
      </Route>

      <Route lazy={() => import(`~/components/Layout/index.tsx`)}>
        <Route path="*" lazy={() => import('./pages/Error404/index.tsx')} />
      </Route>
    </>,
  ),
  {
    future: {
      v7_skipActionErrorRevalidation: true,
      v7_partialHydration: true,
      v7_normalizeFormMethod: true,
      v7_fetcherPersist: true,
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  },
)

function Console() {
  /**
   * Hey you, if you update these console navigation links, please also update the console search items
   * in _src/javascripts/sharedWithConsole/TlDocSearch.tsx that go along with them.
   * */

  return (
    <StrictMode>
      <SWRConfig value={{ fetcher: apiFetch, use: [unwrapSWRResponse] }}>
        <HelmetProvider>
          {process.env.NODE_ENV === 'development' && <div id="debug" />}
          <RouterProvider router={router} />
        </HelmetProvider>
      </SWRConfig>
    </StrictMode>
  )
}

export const ConsoleWithProfiler = withProfiler(Console)
